import cx from 'classnames';
import noop from 'lodash/noop';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import { observer } from 'mobx-react';
import Avatar from '../../../components/Avatar';
import Icon from '../../../components/Icon';
import ProgressSpinner from '../../../components/ProgressSpinner';
import { DELIVERED, SENDING, SENT } from '../../../consts/MessageReceiptState';
import { toArray } from '../../../utils/arrayUtil';
import './styles.sass';
import { CallPrompt } from '../../calling/views/CallPrompt/index.';
import PromptMessageContent from './PromptMessageContent';
import JobProgressionMessageContent from './JobProgressionMessageContent';
var EMPTY = [];

/**
 * Message component.
 *
 * @param  {ReactElement} options.children
 * The message
 *
 * @param  {Boolean} options.mine
 * Was this message from "me"?
 *
 * @param {string} className
 * Additional class name to add.
 *
 * @param  {Boolean} options.animateEntrance
 * Should we animate the message when it mounts?
 *
 * @param  {string} options.name
 * Sender name - should only be used by "theirs".
 *
 * @param  {string} options.avatar
 * URL of the avatar to display, if any. Should also only be used for "theirs".
 *
 * @param  {string[]} options.seenBy
 * Array of picture URL's. Usually only used on the last message that was seen.
 *
 * @param {*} options.sentAt
 * Anything that can be parsed as a moment. If falsy, won't be displayed.
 *
 * @param {"SENDING" | "SENT" | "DELIVERED" | undefined} options.receiptState
 * When set, displays the correct indicator.
 *
 * @param  {object} options.style
 * A style object being applied on the bubble.
 *
 * @param {Function} onClick
 * The click handler for clicking on a bubble.
 */
var Message = observer(function (_ref) {
  var children = _ref.children,
    className = _ref.className,
    animateEntrance = _ref.animateEntrance,
    mine = _ref.mine,
    name = _ref.name,
    avatar = _ref.avatar,
    seenBy = _ref.seenBy,
    sentAt = _ref.sentAt,
    receiptState = _ref.receiptState,
    style = _ref.style,
    onClick = _ref.onClick,
    onDelete = _ref.onDelete,
    type = _ref.type,
    id = _ref.id;
  seenBy = seenBy ? toArray(seenBy) : EMPTY;
  var extraText = '';
  switch (type) {
    case 'ADMIN':
    case 'ADMIN_NOTE':
      extraText = '• Admin';
      break;
    case 'TEAM_NOTE':
      extraText = '• Team';
      break;
  }
  return /*#__PURE__*/React.createElement("div", {
    className: cx('Message', {
      'Message--mine': mine,
      'Message--theirs': !mine,
      'Message--animate-entrance': animateEntrance
    }, className)
  }, /*#__PURE__*/React.createElement("div", {
    className: "Message__gutter Message__gutter--left"
  }, avatar && /*#__PURE__*/React.createElement(Avatar, {
    url: avatar,
    className: "Message__sender-avatar"
  })), /*#__PURE__*/React.createElement("div", {
    onClick: onClick,
    className: "Message__bubble-container"
  }, /*#__PURE__*/React.createElement("div", {
    className: "Message__bubble-package"
  }, name && /*#__PURE__*/React.createElement("div", {
    className: "Message__sender-name"
  }, name, " ", extraText), children, sentAt && /*#__PURE__*/React.createElement("div", {
    className: "Message__sent-timestamp"
  }, "sent ", moment(sentAt).format('LL LT')))), /*#__PURE__*/React.createElement("div", {
    className: "Message__gutter Message__gutter--right"
  }, !!mine && seenBy.length === 0 && /*#__PURE__*/React.createElement(ReceiptState, {
    receiptState: receiptState
  }), seenBy.map(function (s) {
    return /*#__PURE__*/React.createElement(Avatar, {
      className: "Message__seen-by-avatar",
      key: s,
      url: s
    });
  })));
});
Message.defaultProps = {
  onClick: noop
};
Message.propTypes = {
  children: PropTypes.node,
  mine: PropTypes.bool,
  name: PropTypes.any,
  avatar: PropTypes.string,
  animateEntrance: PropTypes.bool,
  className: PropTypes.string,
  seenBy: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  sentAt: PropTypes.any,
  receiptState: PropTypes.string,
  style: PropTypes.object,
  onClick: PropTypes.any,
  onDelete: PropTypes.any
};
export default Message;

/**
 * Indicates the status of the message.
 */
var ReceiptState = function ReceiptState(_ref2) {
  var receiptState = _ref2.receiptState;
  var sending = receiptState === SENDING;
  if (sending) {
    return /*#__PURE__*/React.createElement(ProgressSpinner, {
      blue: true,
      size: 12,
      thickness: 2
    });
  }
  var sent = receiptState === SENT;
  var delivered = receiptState === DELIVERED;
  if (!sent && !delivered) {
    return false;
  }
  var icon = delivered ? 'check circle' : 'send';
  var title = delivered ? 'delivered' : 'sent';
  var color = delivered ? '#2395F3' : '#bbb';
  return /*#__PURE__*/React.createElement(Icon, {
    material: true,
    size: 12,
    name: icon,
    color: color,
    title: title
  });
};
ReceiptState.propTypes = {
  receiptState: PropTypes.string
};
export var MessageBubble = observer(function (_ref3) {
  var children = _ref3.children,
    type = _ref3.type;
  var bubbleClassName = '';
  var contentClassName = '';
  switch (type) {
    case 'admin':
      bubbleClassName = 'admin';
      contentClassName = 'admin__textcolor';
      break;
    case 'note':
      bubbleClassName = 'note';
      contentClassName = 'note__textcolor';
      break;
  }
  return /*#__PURE__*/React.createElement("div", {
    className: cx('Message__bubble', bubbleClassName)
  }, /*#__PURE__*/React.createElement("div", {
    className: cx('Message__content', contentClassName),
    dangerouslySetInnerHTML: {
      __html: children
    },
    "data-test": "message-content"
  }));
});
MessageBubble.propTypes = {
  children: PropTypes.node,
  type: PropTypes.string
};
export var CallMessageBubble = observer(function CallMessageBubble(_ref4) {
  var message = _ref4.message;
  var call = message.content.call;
  return /*#__PURE__*/React.createElement("div", null, call ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "CallMessage__text"
  }, "Call"), /*#__PURE__*/React.createElement("div", {
    className: "CallMessage__prompt"
  }), /*#__PURE__*/React.createElement(CallPrompt, {
    bordered: true,
    call: call
  })) : /*#__PURE__*/React.createElement(MessageBubble, null, "Call Started"));
});
export var PromptMessageBubble = observer(function PromptMessageBubble(_ref5) {
  var message = _ref5.message,
    onViewPrompt = _ref5.onViewPrompt;
  var prompt = message.content.prompt;
  return /*#__PURE__*/React.createElement("div", null, prompt ? /*#__PURE__*/React.createElement(PromptMessageContent, {
    prompt: message.content.prompt,
    onViewPrompt: onViewPrompt
  }) : /*#__PURE__*/React.createElement(MessageBubble, null, "Prompt"));
});
export var JobProgressionMessageBubble = observer(function JobProgressionMessageBubble(_ref6) {
  var message = _ref6.message,
    onViewRejectDraft = _ref6.onViewRejectDraft,
    onViewApproveDraft = _ref6.onViewApproveDraft,
    isReadOnly = _ref6.isReadOnly;
  var jobProgression = message.content.progression;
  var stepId = message.content.stepId;
  var jobProgressionStep = jobProgression.progressionSteps.find(function (s) {
    return s.id === stepId;
  });
  return /*#__PURE__*/React.createElement("div", null, jobProgressionStep ? /*#__PURE__*/React.createElement(JobProgressionMessageContent, {
    jobProgressionStep: jobProgressionStep,
    onViewRejectDraft: onViewRejectDraft,
    onViewApproveDraft: onViewApproveDraft,
    isReadOnly: isReadOnly
  }) : /*#__PURE__*/React.createElement(MessageBubble, null, "Job Progression"));
});
//# sourceMappingURL=Message.js.map
